import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db } from "../../helpers/firebase";
import { getAuth } from "firebase/auth";
import { expensesTable } from "../../helpers/tables";

/**
 * Upload a receipt file (image or PDF) to storage and return its download URL.
 * @param {File} file - Receipt file (image/* or application/pdf)
 * @returns {Promise<string>} Download URL
 */
export async function uploadReceipt(file) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("User not authenticated");

  const storage = getStorage();
  const ext =
    (file.name && file.name.split(".").pop()) ||
    (file.type === "application/pdf" ? "pdf" : "jpg");
  const path = `expense-receipts/${user.uid}/${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 10)}.${ext}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
}

/**
 * Create a manual expense
 * @param {Object} expenseData - { date, payee, amount, unitId, category, description?, receiptUrl? }
 * @returns {Promise<{ success: boolean, id?: string, error?: string }>}
 */
export async function createExpense(expenseData) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const toDate = (v) => {
      if (!v) return new Date();
      if (v instanceof Date) return v;
      if (v.toDate && typeof v.toDate === "function") return v.toDate();
      if (v.$d) return v.$d; // dayjs
      const d = new Date(v);
      return isNaN(d.getTime()) ? new Date() : d;
    };

    const expense = {
      ...expenseData,
      ownerId: user.uid,
      date: Timestamp.fromDate(toDate(expenseData.date)),
      amount: Number(expenseData.amount) || 0,
      payee: expenseData.payee || "",
      unitId: expenseData.unitId || "",
      category: expenseData.category || "",
      description: expenseData.description || "",
      receiptUrl: expenseData.receiptUrl || "",
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const cleanedExpense = Object.fromEntries(
      Object.entries(expense).filter(([_, value]) => value !== undefined)
    );

    const docRef = await addDoc(collection(db, expensesTable), cleanedExpense);
    return { success: true, id: docRef.id, ...cleanedExpense };
  } catch (error) {
    console.error("Error creating expense:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Get expenses for the current user (owner)
 * @param {Object} options - { unitId?, startDate?, endDate? }
 * @returns {Promise<{ success: boolean, expenses?: Array }>}
 */
export async function getExpensesByOwner(options = {}) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const q = query(
      collection(db, expensesTable),
      where("ownerId", "==", user.uid)
    );

    const snapshot = await getDocs(q);
    let expenses = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate?.() || doc.data().date,
    }));

    // Sort by date descending (most recent first)
    expenses.sort((a, b) => {
      const dA = a.date instanceof Date ? a.date : new Date(a.date);
      const dB = b.date instanceof Date ? b.date : new Date(b.date);
      return dB.getTime() - dA.getTime();
    });

    if (options.unitId) {
      expenses = expenses.filter((e) => e.unitId === options.unitId);
    }
    if (options.startDate) {
      const start =
        options.startDate instanceof Date
          ? options.startDate
          : new Date(options.startDate);
      expenses = expenses.filter((e) => {
        const d = e.date instanceof Date ? e.date : new Date(e.date);
        return d >= start;
      });
    }
    if (options.endDate) {
      const end =
        options.endDate instanceof Date
          ? options.endDate
          : new Date(options.endDate);
      expenses = expenses.filter((e) => {
        const d = e.date instanceof Date ? e.date : new Date(e.date);
        return d <= end;
      });
    }

    return { success: true, expenses };
  } catch (error) {
    console.error("Error getting expenses:", error);
    return { success: false, error: error.message, expenses: [] };
  }
}
